import React from "react";
import type { FieldValue, SimpleFieldConfig } from "../../types";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { MultiUserSelect } from "@/components/shared/selects/MultiUserSelect";
import { TextFilterInput } from "../advanced-filters/components/TextFilterInput";
import { DateFilterInput } from "../advanced-filters/components/DateFilterInput";
import { TimeFilterInput } from "../advanced-filters/components/TimeFilterInput";
import { NumberFilterInput } from "../advanced-filters/components/NumberFilterInput";
import { SelectFilterInput } from "../advanced-filters/components/SelectFilterInput";
import type { CurrentFilter } from "./filter-state";

/**
 * Picks exactly ONE input for a field.
 *
 * Split out of `AdvancedFilterPanel.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/AdvancedFilterPanel*.test.tsx`.
 *
 * PRECEDENCE IS THE CONTRACT, and it prevents a historical bug where a field
 * with both `options` and a `type` (or a special `filterType`) rendered two
 * inputs and two Clear buttons stacked on top of each other:
 *
 *   1. `filterType` (company / client / user select)
 *   2. `options`    (operator + value select)
 *   3. `type`       (text / date / time / number)
 *   4. nothing      — the caller skips the whole grid cell
 */
export function renderFilterFieldInput({
  fieldId,
  fieldConfig,
  currentFilter,
  addAdvancedFilter,
  removeAdvancedFilter,
}: {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  currentFilter: CurrentFilter;
  addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
  removeAdvancedFilter: (fieldId: string) => void;
}): React.ReactNode {
  switch (fieldConfig.filterType) {
    case "company-select":
      return (
        <CompanySelectWithModal
          value={typeof currentFilter?.value === "string" ? currentFilter.value : ""}
          onValueChange={(value) => addAdvancedFilter(fieldId, "company-select", value)}
          placeholder={`Filter by ${fieldConfig.label}...`}
        />
      );
    case "client-select":
      return (
        <ClientSelectWithModal
          value={typeof currentFilter?.value === "string" ? currentFilter.value : ""}
          onValueChange={(value) => addAdvancedFilter(fieldId, "client-select", value)}
          placeholder={`Filter by ${fieldConfig.label}...`}
        />
      );
    case "user-select":
      return (
        <MultiUserSelect
          currentUserIds={Array.isArray(currentFilter?.value) ? currentFilter.value : []}
          onUsersSelect={async (userIds) => {
            // An emptied selection must CLEAR the filter, not filter on [] —
            // which would match nothing and read as "everything disappeared".
            if (!userIds || userIds.length === 0) {
              removeAdvancedFilter(fieldId);
            } else {
              addAdvancedFilter(fieldId, "user-select", userIds);
            }
          }}
          placeholder={`Filter by ${fieldConfig.label}...`}
          showUnassigned={false}
          showAllUsers={false}
          maxUsers={10}
          expandInlineVariant="replace"
          meOptionLabel="Me"
          meOptionDescription="Filter to records tied to you"
        />
      );
  }

  if (fieldConfig.options) {
    return (
      <SelectFilterInput
        fieldId={fieldId}
        fieldConfig={fieldConfig}
        currentFilter={currentFilter}
        addAdvancedFilter={addAdvancedFilter}
      />
    );
  }

  switch (fieldConfig.type) {
    case "text":
      return (
        <TextFilterInput
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          currentFilter={currentFilter}
          addAdvancedFilter={addAdvancedFilter}
        />
      );
    case "date":
      return (
        <DateFilterInput
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          currentFilter={currentFilter}
          addAdvancedFilter={addAdvancedFilter}
        />
      );
    case "time":
      return (
        <TimeFilterInput
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          currentFilter={currentFilter}
          addAdvancedFilter={addAdvancedFilter}
        />
      );
    case "number":
      return (
        <NumberFilterInput
          fieldId={fieldId}
          fieldConfig={fieldConfig}
          currentFilter={currentFilter}
          addAdvancedFilter={addAdvancedFilter}
        />
      );
    default:
      return null;
  }
}
